import { useState, useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { MessageSquare, Upload, Mic, FileText } from 'lucide-react'
import { GapFillView } from '@/components/onboarding/GapFillView'
import { DocumentUploadView } from '@/components/onboarding/DocumentUploadView'
import { AudioRecordView } from '@/components/onboarding/AudioRecordView'
import { TextInputView } from '@/components/onboarding/TextInputView'
import { DomainContextHeader } from '@/components/context/DomainContextHeader'
import { ALL_DOMAINS } from '@/types/contextEngine'
import type { ContextDomain } from '@/types/contextEngine'

export type InputMode = 'interview' | 'upload' | 'record' | 'text'

const VALID_MODES: InputMode[] = ['interview', 'upload', 'record', 'text']

const INPUT_MODES: { id: InputMode; label: string; icon: typeof MessageSquare; description: string }[] = [
  { id: 'interview', label: 'Interview', icon: MessageSquare, description: 'AI-guided gap filling' },
  { id: 'upload', label: 'Upload', icon: Upload, description: 'PDF, PPTX, or TXT' },
  { id: 'record', label: 'Record', icon: Mic, description: 'Voice note' },
  { id: 'text', label: 'Write', icon: FileText, description: 'Paste or type' },
]

function parseMode(param: string | null): InputMode {
  if (param && VALID_MODES.includes(param as InputMode)) {
    return param as InputMode
  }
  return 'interview'
}

function parseDomain(param: string | null): ContextDomain | null {
  if (param && ALL_DOMAINS.includes(param as ContextDomain)) {
    return param as ContextDomain
  }
  return null
}

export function AddContextPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const modeParam = searchParams.get('mode')
  const domainParam = searchParams.get('domain')
  const gapParam = searchParams.get('gap')

  const [mode, setMode] = useState<InputMode>(() => parseMode(modeParam))
  const domain = parseDomain(domainParam)

  // Sync mode state when URL params change (e.g. browser back/forward)
  useEffect(() => {
    const parsed = parseMode(modeParam)
    setMode(parsed)
  }, [modeParam])

  const handleModeChange = useCallback(
    (newMode: InputMode) => {
      setMode(newMode)
      const params = new URLSearchParams(searchParams)
      params.set('mode', newMode)
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const handleComplete = useCallback(() => {
    if (domain) {
      navigate(`/context/domains/${domain}`)
    } else {
      navigate('/context')
    }
  }, [navigate, domain])

  return (
    <div className="mx-auto max-w-3xl p-6 lg:p-8">
      {/* Domain context header (when deep-linked from a gap card) */}
      {domain && <DomainContextHeader domain={domain} gapId={gapParam} />}

      <div className="mb-5 flex items-baseline justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Add Context</h1>
        <p className="text-sm text-text-tertiary">
          Choose an input method below
        </p>
      </div>

      {/* Mode selector */}
      <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {INPUT_MODES.map(({ id, label, icon: Icon, description }) => (
          <button
            key={id}
            onClick={() => handleModeChange(id)}
            className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
              mode === id
                ? 'border-accent-amber bg-accent-amber/10 text-accent-amber'
                : 'border-border bg-bg-elevated text-text-secondary hover:border-border-hover hover:text-text-primary'
            }`}
          >
            <Icon size={18} className="shrink-0" />
            <div className="min-w-0">
              <span className="text-sm font-medium block">{label}</span>
              <span className="text-xs opacity-60 block truncate">{description}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Active input view */}
      {mode === 'interview' && <GapFillView onComplete={handleComplete} />}
      {mode === 'upload' && <DocumentUploadView onComplete={handleComplete} />}
      {mode === 'record' && <AudioRecordView onComplete={handleComplete} />}
      {mode === 'text' && <TextInputView onComplete={handleComplete} />}
    </div>
  )
}
